import { useCallback, useEffect, useRef, useState } from 'react';
import { usePredictLicensePlate } from '@/core/domain/vision/predict/predict.hooks';
import { Button, Image, message, Upload } from 'antd';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { FaCamera, FaTrash } from 'react-icons/fa';
import { RcFile } from 'antd/lib/upload';
import { compressImage } from '@/utils/functions/compressImage';

const MIME_TYPES = ['image/png', 'image/jpg', 'image/jpeg'];

interface Props {
  onChangeLicensePlate(value?: string): void;
}

export default function VisionCam({
  onChangeLicensePlate: changeLicensePlate,
}: Props) {
  const predictLicensePlate = usePredictLicensePlate();

  const [licensePlate, setLicensePlate] = useState<string>();
  const [expanded, setExpanded] = useState(false);
  const [fileUpload, setFileUpload] = useState<File | RcFile>();

  useEffect(() => {
    changeLicensePlate(licensePlate);
  }, [licensePlate]);

  const onCapture = useCallback(async (file: File | RcFile) => {
    try {
      if (file) {
        setExpanded(false);
        return await getLicensePlate(file);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (!!fileUpload) onCapture(fileUpload);
  }, [fileUpload]);

  async function getLicensePlate(file: File | RcFile) {
    try {
      const compressedFile = await compressImage(file as File);

      const res = await predictLicensePlate.mutateAsync({
        image: compressedFile,
      });

      setLicensePlate(res.licensePlate);
    } catch {
      handleRemoveImage();
    }
  }

  const handleRemoveImage = () => {
    setFileUpload(undefined);
    setLicensePlate(undefined);
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.click();
  }, []);

  return (
    <div data-expanded={expanded}>
      {predictLicensePlate.isLoading && (
        <ModalLoadingOverlay
          messages={[
            'Analisando imagem capturada...',
            'Processando placa do veículo...',
            'Extraindo dados da placa...',
            'Verificando padrão de placa...',
            'Procurando veículo na base...',
            'Carregando...',
            'Carregando...',
            'Carregando...',
          ]}
        />
      )}

      {!!fileUpload && (
        <div style={{ maxWidth: 400, margin: 'auto' }}>
          <Image
            src={URL.createObjectURL(fileUpload)}
            preview={false}
            alt="Imagem carregada"
            style={{ borderRadius: 8, marginBottom: 12 }}
          />

          <Button block icon={<FaTrash />} onClick={handleRemoveImage} danger>
            Excluir Imagem
          </Button>
        </div>
      )}

      {!fileUpload && (
        <Upload.Dragger
          style={{ padding: 32 }}
          maxCount={1}
          beforeUpload={(file) => {
            if (!MIME_TYPES.includes(file.type)) {
              message.destroy();
              message.error('Tipo de arquivo inválido, permitido PNG e JPG');
            }

            return MIME_TYPES.includes(file.type) || Upload.LIST_IGNORE;
          }}
          onRemove={handleRemoveImage}
          customRequest={({ onSuccess, file }) => {
            setFileUpload(file as RcFile);
            if (onSuccess) {
              onSuccess(file);
            }
          }}
          listType="picture-card"
          capture="environment"
          showUploadList={false}
        >
          <input ref={inputRef} style={{ display: 'none' }} />
          <FaCamera size={32} />
          <p style={{ marginTop: 10 }}>
            Capture uma placa para dar entrada no veículo
          </p>
        </Upload.Dragger>
      )}
    </div>
  );
}
